<template>
  <div>
    <v-alert type="info" dense outlined text class="mb-4">
      Attach at most one analytics config of each type. The same catalog config can be reused on reskins.
      Manage catalog entries on
      <router-link to="/analytics-configs">Analytics configs</router-link>.
    </v-alert>

    <div v-if="loading" class="d-flex justify-center pa-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else>
      <v-card
        v-for="slot in typeSlots"
        :key="slot.type"
        class="mb-4 game-form__section"
        elevation="1"
      >
        <v-card-title class="game-form__section-title d-flex align-center">
          <v-icon class="mr-2">mdi-chart-timeline-variant</v-icon>
          <span>{{ slot.label }}</span>
          <v-tooltip v-if="slot.link" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                class="ml-1"
                color="secondary"
                tag="a"
                :href="linkChangeLogHref(slot)"
                target="_blank"
                rel="noopener noreferrer"
                :data-testid="`game-analytics-history-${slot.type}`"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-history</v-icon>
              </v-btn>
            </template>
            <span>View change history</span>
          </v-tooltip>
          <v-spacer />
          <v-chip
            small
            label
            :color="slot.link ? 'success' : 'grey'"
            :outlined="!slot.link"
          >
            {{ slot.link ? 'Attached' : 'Not set' }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <template v-if="slot.link">
            <v-row dense>
              <v-col cols="12" md="6">
                <div class="text--secondary" style="font-size: 0.75rem">Key</div>
                <div class="text-break">{{ slot.link.analyticsConfig?.key || '—' }}</div>
              </v-col>
              <v-col cols="6" md="2">
                <div class="text--secondary" style="font-size: 0.75rem">Sampling %</div>
                <div>{{ slot.link.analyticsConfig?.sampling ?? '—' }}</div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="text--secondary" style="font-size: 0.75rem">Description</div>
                <div>{{ slot.link.analyticsConfig?.description || '—' }}</div>
              </v-col>
            </v-row>
            <div
              v-if="(slot.link.analyticsConfig?.domainSamplings || []).length"
              class="mt-3"
            >
              <div class="text--secondary mb-1" style="font-size: 0.75rem">Domain overrides</div>
              <v-chip
                v-for="row in slot.link.analyticsConfig.domainSamplings"
                :key="row.id || row.domain"
                class="mr-2 mb-1"
                small
                outlined
              >
                {{ row.domain }} · {{ row.sampling }}%
              </v-chip>
            </div>
            <div class="d-flex align-center mt-4">
              <v-btn
                small
                color="primary"
                outlined
                class="mr-2"
                :to="catalogLink(slot.link.analyticsConfigId)"
              >
                Open in catalog
              </v-btn>
              <v-btn
                small
                color="warning"
                outlined
                :loading="slot.busy"
                :data-testid="`game-analytics-unlink-${slot.type}`"
                @click="unlink(slot)"
              >
                Unlink
              </v-btn>
            </div>
          </template>

          <template v-else>
            <v-row dense align="center">
              <v-col cols="12" md="8">
                <v-autocomplete
                  v-model="slot.selectedId"
                  :items="catalogOptions(slot.type)"
                  item-text="label"
                  item-value="id"
                  label="Select catalog config"
                  dense
                  clearable
                  hide-details="auto"
                  :loading="catalogLoading"
                  :data-testid="`game-analytics-select-${slot.type}`"
                />
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-center">
                <v-btn
                  color="primary"
                  class="mr-2"
                  :disabled="!slot.selectedId"
                  :loading="slot.busy"
                  :data-testid="`game-analytics-attach-${slot.type}`"
                  @click="attach(slot)"
                >
                  Attach
                </v-btn>
                <v-btn
                  text
                  small
                  color="primary"
                  :to="createCatalogLink(slot.type)"
                >
                  Create new
                </v-btn>
              </v-col>
            </v-row>
            <p v-if="!catalogOptions(slot.type).length" class="text--secondary mt-2 mb-0" style="font-size: 0.8125rem">
              No {{ slot.label }} configs in the catalog yet.
            </p>
          </template>
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script>
import {
  ANALYTICS_CONFIG_TYPES,
  apiErrorMessage,
} from '../../../constants/analyticsConfigTypes';

export default {
  name: 'GameAnalyticsTab',
  props: {
    game: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      catalogLoading: false,
      links: [],
      catalog: [],
      typeSlots: ANALYTICS_CONFIG_TYPES.map((t) => ({
        type: t.value,
        label: t.text,
        link: null,
        selectedId: null,
        busy: false,
      })),
    };
  },
  watch: {
    'game.id': {
      immediate: true,
      handler(id) {
        if (id) this.reload();
      },
    },
  },
  methods: {
    returnToGame() {
      return `/games/${this.game.id}?tab=analytics`;
    },
    catalogLink(configId) {
      return {
        path: `/analytics-configs/${configId}`,
        query: { returnTo: this.returnToGame() },
      };
    },
    createCatalogLink(type) {
      return {
        path: '/analytics-configs/new',
        query: { type, returnTo: this.returnToGame() },
      };
    },
    linkChangeLogLink(slot) {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'game_analytics_config',
          // Composite PK is stored as JSON.stringify(pkMap) in the change log.
          entityId: JSON.stringify({
            gameId: Number(this.game.id),
            analyticsConfigId: Number(slot.link.analyticsConfigId),
          }),
        },
      };
    },
    linkChangeLogHref(slot) {
      return this.$router.resolve(this.linkChangeLogLink(slot)).href;
    },
    catalogOptions(type) {
      return this.catalog
        .filter((c) => c.type === type)
        .map((c) => {
          const parts = [`#${c.id}`, c.key];
          if (c.description) parts.push(c.description);
          return {
            id: c.id,
            label: parts.join(' · '),
          };
        });
    },
    syncSlots() {
      this.typeSlots = ANALYTICS_CONFIG_TYPES.map((t) => {
        const prev = this.typeSlots.find((s) => s.type === t.value);
        const link = this.links.find((l) => l.type === t.value) || null;
        return {
          type: t.value,
          label: t.text,
          link,
          selectedId: prev && !link ? prev.selectedId : null,
          busy: false,
        };
      });
    },
    async reload() {
      if (!this.game?.id) return;
      this.loading = true;
      try {
        await Promise.all([this.loadLinks(), this.loadCatalog()]);
        this.syncSlots();
      } finally {
        this.loading = false;
      }
    },
    async loadLinks() {
      try {
        const list = await this.$gameApi.get(`/games/${this.game.id}/analytics-configs`);
        this.links = Array.isArray(list) ? list : [];
      } catch (e) {
        this.links = [];
        this.$swal.fire({
          icon: 'error',
          title: apiErrorMessage(e, 'Failed to load game analytics'),
          timer: 2500,
          showConfirmButton: false,
        });
      }
    },
    async loadCatalog() {
      this.catalogLoading = true;
      try {
        const list = await this.$gameApi.get('/analytics-configs');
        this.catalog = Array.isArray(list) ? list : [];
      } catch (e) {
        this.catalog = [];
        this.$swal.fire({
          icon: 'error',
          title: apiErrorMessage(e, 'Failed to load analytics catalog'),
          timer: 2500,
          showConfirmButton: false,
        });
      } finally {
        this.catalogLoading = false;
      }
    },
    async attach(slot) {
      if (!slot.selectedId || !this.game?.id) return;
      slot.busy = true;
      try {
        await this.$gameApi.post(`/games/${this.game.id}/analytics-configs/${slot.selectedId}`, {});
        this.$swal.fire({
          icon: 'success',
          title: 'Attached',
          timer: 1200,
          showConfirmButton: false,
        });
        await this.loadLinks();
        this.syncSlots();
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: apiErrorMessage(e, 'Attach failed'),
          timer: 3500,
          showConfirmButton: false,
        });
      } finally {
        slot.busy = false;
      }
    },
    async unlink(slot) {
      if (!slot.link || !this.game?.id) return;
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: `Unlink ${slot.label}?`,
        text: 'The catalog config itself will not be deleted.',
        showCancelButton: true,
        confirmButtonText: 'Unlink',
      });
      if (!isConfirmed) return;
      slot.busy = true;
      try {
        await this.$gameApi.delete(
          `/games/${this.game.id}/analytics-configs/${slot.link.analyticsConfigId}`,
        );
        this.$swal.fire({
          icon: 'success',
          title: 'Unlinked',
          timer: 1200,
          showConfirmButton: false,
        });
        await this.loadLinks();
        this.syncSlots();
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: apiErrorMessage(e, 'Unlink failed'),
          timer: 3500,
          showConfirmButton: false,
        });
      } finally {
        slot.busy = false;
      }
    },
  },
};
</script>
